import * as React from 'react';
import * as UI from 'core/ui';
import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { InstallationType, Permission, Res } from 'core/api';
import { Command } from 'pos/common';
import * as SalesStore from '../SalesStore';
import { PosScreen, Header, Content, Footer, PhoneOnly, BackActionButton } from 'pos/ui';
import { RestaurantFacade } from 'pos/facades';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { Box, VStack, Text } from '@chakra-ui/react';
import { BasicButton, CardPanel } from 'core/ui-next';

type Props = { cancellationReasons: string[]; } & Modal.Actions<string>;

const stateSchema = z.object({
	selectedReason: z.string().refine(val => val !== '', { message: Res.Msg.MSG_VALUE_REQUIRED() }),
	reason: z.string().max(255, { message: Res.Msg.MSG_MAX_LENGTH_EXCEEDED(255) })
}).refine((data) => {
	if (data.selectedReason === Res.Msg.MSG_CUSTOM_OPTION()) {
		return data.reason.trim() !== '';
	}
	return true;
}, { message: Res.Msg.MSG_VALUE_REQUIRED(), path: ['reason'] });

type State = z.infer<typeof stateSchema>;

function CreateLoss(props: Props) {
	const { cancellationReasons, cancel, close } = props;
	const { Form, Select, TextArea } = useControlsFor<State>();
	const formRef = React.useRef<FormRef<State>>(null);
	const [reasonValues, setReasonValues] = React.useState<State>({
		selectedReason: cancellationReasons[0],
		reason: ''
	});

	const onSelectedReasonChanged = async (selectedReason: string) => {
		setReasonValues(prev => ({ ...prev, selectedReason }));
	};

	const onOK = async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		close(reasonValues.selectedReason !== Res.Msg.MSG_CUSTOM_OPTION()
			? reasonValues.selectedReason
			: result.value.reason
		);
	};

	const onCancel = React.useCallback(async () => cancel(), [cancel]);
	return (
		<PosScreen screenId={'CREATE_LOSS'} size={'small'} useKeyPad={false}>
			<Header title={Res.Web.CREATE_LOSS()} onClose={cancel} />
			<Content>
				<VStack width={'full'} height={'full'} padding={'6'} gap={'6'} overflow={'auto'}>
					<CardPanel width={'full'} height={'21'} padding={'4'} justifyContent={'center'} alignItems={'center'} shadow={'md'}>
						<Text fontSize={'md'} fontWeight={'bold'} color={{ base: 'neutral.600', _dark: 'neutral.400' }}>
							{Res.Web.CREATE_LOSS_TICKET_LBL().toUpperCase()}
						</Text>
						<Text fontSize={'2xl'} fontWeight={'bold'}>
							{UI.Format.asMoney(SalesStore.getTicket().NetAmount)}
						</Text>
					</CardPanel>
					<Box width={'full'}>
						<Form ref={formRef} initialValue={reasonValues} schema={stateSchema}>
							<VStack width={'full'} gap={'4'}>
								<Select linkTo={'selectedReason'} label={Res.Web.CREATE_LOSS_REASON_LBL()} placeholder={Res.Web.CREATE_LOSS_REASON_LBL()} options={cancellationReasons} renderOption={opt => opt ? opt : ''} valueGetter={opt => opt ? opt : ''} onWatch={onSelectedReasonChanged} required />
								{reasonValues.selectedReason === Res.Msg.MSG_CUSTOM_OPTION() ?
									<TextArea linkTo={'reason'} placeholder={Res.Web.CREATE_LOSS_REASON_PLACEHOLDER()} rows={8} onKeyDown={UI.whenEnterKey(onOK)} maxLength={120} />
									: null
								}
							</VStack>
						</Form>
					</Box>
					<BasicButton type='success' width={'full'} text={Res.Common.OK_BTN()} onClick={onOK} />
				</VStack>
			</Content>
			<Footer>
				<PhoneOnly>
					<BackActionButton action={onCancel} />
				</PhoneOnly>
			</Footer>
		</PosScreen>
	);
}

declare global {
	interface CommandType {
		createLoss: () => Promise<Result>
	}
}

Command.register('createLoss', Preconditions.and(Preconditions.ensure(Permission.Ticket_Loss), Preconditions.ensureIsNotLite), async (): Promise<Result> => {

	const session = SessionProvider.getCurrent();
	// Los hubclient no tienen módulo de stock pero pueden generar mermas aunque tengan el módulo activo, ya que es el ACMS quien procesa
	// la info subida.	
	if (!session.EnableStockModule && session.InstallationType !== InstallationType.HubClient) {
		await Message.show(Res.Msg.MSG_TICKET_IS_EMPTY());
		return Result.error();
	}


	const ticket = SalesStore.getTicket();
	if (ticket.IsEmpty) {
		await Message.show(Res.Msg.MSG_TICKET_IS_EMPTY());
		return Result.error();
	}

	const cancellationReasons = await RestaurantFacade.getLossReasons();
	const result = await Modal.show(CreateLoss, { cancellationReasons });

	if (!result.ok)
		return Result.error();

	await SalesStore.createLoss(result.value);
	return Result.ok();
});
